import { Card, CardContent } from '../../../components/ui/card';
import { Check, RefreshCw, ShieldCheck, AlertOctagon, AlertTriangle } from 'lucide-react';
import { DuplicateCard } from './DuplicateCard';

const TIER_GROUPS = [
  { key: 'exact', label: 'Exact Duplicates', helper: 'Same name + DOB + parent · auto-merge eligible', Icon: ShieldCheck, color: 'text-rose-700 bg-rose-50 border-rose-200' },
  { key: 'strong', label: 'Strong Matches', helper: 'Same name + DOB · parent differs/missing · review before merge', Icon: AlertOctagon, color: 'text-amber-800 bg-amber-50 border-amber-200' },
  { key: 'possible', label: 'Possible Matches', helper: 'Soft signals · review only', Icon: AlertTriangle, color: 'text-slate-700 bg-slate-50 border-slate-200' },
];

export const PlayerDuplicatesTab = ({ loading, duplicates, onMerge, onIgnore, ignoring, summary }) => {
  if (loading) {
    return (
      <div className="text-center py-8">
        <RefreshCw className="h-8 w-8 animate-spin mx-auto text-slate-400" />
        <p className="text-slate-500 mt-2">Scanning for duplicates...</p>
      </div>
    );
  }

  if (duplicates.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <Check className="h-12 w-12 mx-auto text-green-500 mb-3" />
          <p className="text-lg font-medium text-green-700">No duplicate players found</p>
          <p className="text-slate-500">All player records appear to be unique.</p>
        </CardContent>
      </Card>
    );
  }

  // Group by tier so each section renders with its own header.
  const byTier = { exact: [], strong: [], possible: [] };
  duplicates.forEach((d) => {
    const t = d.match_tier || 'possible';
    (byTier[t] || byTier.possible).push(d);
  });

  return (
    <div className="space-y-6">
      {/* Summary header — counts pulled from the API response. */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {TIER_GROUPS.map((g) => {
            const count = summary[`${g.key}_count`] ?? 0;
            const Icon = g.Icon;
            return (
              <div
                key={g.key}
                className={`rounded-md border px-3 py-2 ${g.color}`}
                data-testid={`player-tier-summary-${g.key}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  <span className="text-sm font-semibold">{g.label}</span>
                  <span className="ml-auto text-sm font-bold">{count}</span>
                </div>
                <p className="text-xs opacity-80 mt-0.5">{g.helper}</p>
              </div>
            );
          })}
        </div>
      )}

      {TIER_GROUPS.map(({ key, label, Icon }) => {
        const items = byTier[key];
        if (!items || items.length === 0) return null;
        return (
          <section key={key} className="space-y-2" data-testid={`player-tier-section-${key}`}>
            <div className="flex items-center gap-2 px-1">
              <Icon className="h-4 w-4 text-slate-600" />
              <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">
                {label} <span className="text-slate-500 font-normal">({items.length})</span>
              </h3>
            </div>
            {items.map((dup, idx) => {
              try {
                return (
                  <DuplicateCard
                    key={dup?.player1?.id ? `${dup.player1.id}-${dup.player2.id}` : idx}
                    type="player"
                    dup={dup}
                    onMerge={onMerge}
                    onIgnore={onIgnore}
                    ignoring={ignoring}
                  />
                );
              } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Error rendering player duplicate card:', e, dup);
                return (
                  <Card key={idx} className="border-red-200 bg-red-50">
                    <CardContent className="p-4 text-red-600">
                      Error rendering duplicate record. Please try refreshing.
                    </CardContent>
                  </Card>
                );
              }
            })}
          </section>
        );
      })}
    </div>
  );
};

